import { VuexModule, Module, Mutation, Action } from 'vuex-module-decorators'
import _ from 'lodash'
import { DataTablePagination } from '@/types/_'
import { timeout } from '@/utils/common'
import Apis from '@/Apis/_'

type Mode = 'CREATE' | 'EDIT'

type SetSuporteModal = {
  name: string,
  value: boolean,
}

type SuporteModal = {
  procedimento: boolean,
}

interface Procedimento {
  id: string | null,
  codigo: string,
  descricao: string | null,
  porte: string | null,
  quantidadeAuxiliares: number,
  status: string,
  temLateralidade: boolean,
  tipo: string | null,
  hospitalId: string,
  hisId: string,
  tecnicasDoProcedimento: TecnicaDoProcedimento[],
  tipoTabela?: string,
}

interface TecnicaDoProcedimento {
  id: string,
  descricao: string,
}

@Module({ namespaced: true, name: 'SuporteProcedimentosStore' })
export default class SuporteProcedimentosStore extends VuexModule {
  public hospitalId: string = ''
  public searchProcedimento: string = ''

  public mode: Mode = 'CREATE'

  public modal: SuporteModal = {
    procedimento: false,
  }

  @Mutation
  setSearchProcedimento(val: string) {
    this.searchProcedimento = val
  }

  @Mutation
  clearSearchProcedimento() {
    this.searchProcedimento = ''
  }

  @Mutation
  setModal(modal: SetSuporteModal) {
    this.modal[modal.name] = modal.value
  }

  @Mutation
  setHospitalId(value: string) {
    this.hospitalId = value
  }

  @Mutation
  clearHospitalId() {
    this.hospitalId = ''
  }

  @Mutation
  setMode(param: any) {
    this.mode = param.mode
  }

  /** Procedimentos */
  private procedimentosDefault: Procedimento[] = []
  public procedimentos: Procedimento[] = [...this.procedimentosDefault]
  private procedimentoDefault: Procedimento = {
    id: '',
    codigo: '',
    descricao: null,
    porte: null,
    quantidadeAuxiliares: 0,
    status: 'Ativo',
    temLateralidade: false,
    tipo: null,
    hospitalId: '',
    hisId: '',
    tecnicasDoProcedimento: [],
    tipoTabela: '',
  }

  public procedimento: Procedimento = { ...this.procedimentoDefault }

  private paginationProcedimentoDefault: DataTablePagination = {
    descending: false,
    page: 1,
    rowsPerPage: 5,
    sortBy: null,
    totalItems: 0,
    rowsPerPageItems: [5, 10, 25, 50, 100],
  }

  public paginationProcedimento: DataTablePagination = { ...this.paginationProcedimentoDefault }

  get paginationDefault() {
    return this.paginationProcedimentoDefault
  }

  @Mutation
  setProcedimentos(value: Procedimento[]) {
    this.procedimentos = value
  }

  @Mutation
  clearProcedimentos() {
    this.procedimentos = [...this.procedimentosDefault]
  }

  @Mutation
  setProcedimento(value: Procedimento) {
    this.procedimento = value
  }

  @Mutation
  clearProcedimento() {
    this.procedimento = { ...this.procedimentoDefault }
  }

  @Mutation
  setPaginationProcedimento(val: DataTablePagination) {
    this.paginationProcedimento = val
  }

  @Mutation
  updateProcedimentos(value: Procedimento) {
    const index = this.procedimentos.findIndex((e: Procedimento) => e.id === value.id)
    this.procedimentos[index] = { ...value }
  }

  @Mutation
  insertProcedimento(value: Procedimento) {
    this.procedimentos.push({ ...value })
  }

  @Mutation
  deleteProcedimento(value: Procedimento) {
    const index = this.procedimentos.findIndex((e: Procedimento) => e.id === value.id)
    this.procedimentos[index].status = 'Inativo'
  }

  @Action
  async getProcedimentoPagedSearch(pagination: DataTablePagination) {
    const params: any = {
      hospitalId: this.hospitalId,
      page: pagination.page,
      total: pagination.rowsPerPage,
      searchText: this.searchProcedimento,
    }

    const request = await Apis.Agendamento.SuporteApi.getProcedimentoPagedSearch(params)

    if (request.success) {
      this.context.commit('setProcedimentos', request.data.procedimentos as Procedimento[])
      this.context.commit('setPaginationProcedimento', {
        descending: false,
        page: pagination.page || this.paginationProcedimentoDefault.page,
        rowsPerPage: pagination.rowsPerPage || this.paginationProcedimentoDefault.rowsPerPage,
        sortBy: null,
        totalItems: request.data.total,
        rowsPerPageItems: this.paginationProcedimentoDefault.rowsPerPageItems,
      })
    }

    return request
  }

  @Action
  async upsertProcedimentoHospital() {
    const params: Procedimento = this.procedimento
    params.hospitalId = this.hospitalId

    Object.entries(params).forEach(([key, value]) => {
      if (['', null, undefined].includes(value as any)) delete params[key]
    })

    // O status 'Inativo' do front equivale ao 'Removido' do StatusEnum da API...
    if (params.status && params.status === 'Inativo') params.status = 'Removido'

    const request = await Apis.Agendamento.SuporteApi.upsertProcedimentoHospital(params)
    if (request.success) {
      params.id = request.data.id
    }

    return request
  }

  @Action
  async saveProcedimento() {
    try {
      const params: any = { ...this.procedimento }

      Object.entries(params).forEach(([key, value]) => {
        // @ts-ignore
        if (['', null, undefined].includes(value)) delete params[key]
      })

      const request = await timeout(2000)

      // Precisa retornar um id para inserir na listagem
      // eslint-disable-next-line
      params['id'] = `${Math.floor(Math.random() * 1024 * 1024)}`

      this.context.commit('insertProcedimento', params)

      return { success: true }
    } catch (error) {
      return { success: false }
    }
  }

  @Action
  async updateProcedimento() {
    try {
      const params: any = { ...this.procedimento }

      Object.entries(params).forEach(([key, value]) => {
        if (['', null, undefined].includes(value as any)) delete params[key]
      })

      const request = await timeout(2000)

      this.context.commit('updateProcedimentos', params)

      return { success: true }
    } catch (error) {
      return { success: false }
    }
  }

  @Action
  async apagarProcedimento() {
    try {
      const params: any = { ...this.procedimento }
      await timeout(2000)
      this.context.commit('deleteProcedimento', params)
      return { success: true }
    } catch (error) {
      return { success: false }
    }
  }

  /** Técnicas do procedimento */
  public tecnicasDoProcedimento: TecnicaDoProcedimento[] = []

  @Mutation
  setTecnicasDoProcedimento(value: TecnicaDoProcedimento[]) {
    this.tecnicasDoProcedimento = value
  }

  @Mutation
  clearTecnicasDoProcedimento() {
    this.tecnicasDoProcedimento = []
  }

  @Action
  async getAllTecnicas() {
    try {
      const request = await Apis.Agendamento.SuporteApi.getAllTecnicas(this.hospitalId)
      if (request.success) this.context.commit('setTecnicasDoProcedimento', request.data as TecnicaDoProcedimento[] || [])
      return request
    } catch (error) {
      this.context.commit('setTecnicasDoProcedimento', [])
      return { success: false, message: 'Ocorreu um erro na requisição.' }
    }
  }

  @Action
  async getTecnicasDoProcedimentoByProcedimentoId(procedimentoId: string) {
    try {
      const request = await Apis.Agendamento.SuporteApi.getTecnicasDoProcedimentoByProcedimentoId({
        hospitalId: this.hospitalId,
        procedimentoId,
      })
      if (request.success) this.context.commit('setTecnicasDoProcedimento', request.data.tecnicasDoProcedimento as TecnicaDoProcedimento[] || [])
      return request
    } catch (error) {
      this.context.commit('setTecnicasDoProcedimento', [])
      return { success: false, message: 'Ocorreu um erro na requisição.' }
    }
  }

  @Action
  async upsertProcedimentoTecnica(params: any) {
    try {
      const request = await Apis.Agendamento.SuporteApi.upsertProcedimentoTecnica({
        hospitalId: this.hospitalId,
        tecnicasIds: params.tecnicasIds,
        procedimentoId: params.procedimentoId,
      })
      return request
    } catch (error) {
      return { success: false, message: 'Ocorreu um erro na requisição.' }
    }
  }
}
